// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Typography, CircularProgress } from "@mui/material";
import BackgroundLoader from "../common/BackgroundLoader";
import { useEffect, useState, useCallback } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../slices/store";
import { extendExam, resetExtendExamState } from "../../slices/examSlice/extendExam";
import { RequestState } from "../../utils/types";
import { EXPERT_EXAM_EXTENSION_DAYS, DEFAULT_EXAM_EXTENSION_DAYS } from "../../config/constant";

interface ExtendExamDialogProps {
  examType: string;
  examName: string;
  examRecordId: string;
  onExtend: () => void;
  onCancel: () => void;
}

export default function ExtendExamDialog({ 
  examType, 
  examName, 
  examRecordId,
  onExtend, 
  onCancel 
}: ExtendExamDialogProps) {
  const [open, setOpen] = useState(true);
  const dispatch = useAppDispatch();
  const extendExamState = useAppSelector((state: RootState) => state.extendExam);
  const contactState = useAppSelector((state: RootState) => state.contact);

  // Create stable callback references to prevent unnecessary re-renders
  const stableOnExtend = useCallback(() => {
    onExtend();
  }, [onExtend]);

  const stableOnCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  // Reset state when dialog opens
  useEffect(() => {
    dispatch(resetExtendExamState());
  }, [dispatch]);

  // Handle successful extension and error
  useEffect(() => {
    if (extendExamState.state === RequestState.SUCCESS) {
      setOpen(false);
      stableOnExtend();
    } else if (extendExamState.state === RequestState.FAILED) {
      setOpen(false);
      // Optionally, you can show a snackbar here if not handled globally
      stableOnCancel();
    }
  }, [extendExamState.state, stableOnExtend, stableOnCancel]);

  // Determine extension days based on exam type
  const getExtensionDays = (examType: string): number => {
    // Check if it's an expert exam category
    if (examType && examType.toLowerCase().includes('expert')) {
      return EXPERT_EXAM_EXTENSION_DAYS;
    }
    return DEFAULT_EXAM_EXTENSION_DAYS;
  };

  const extensionDays = getExtensionDays(examType);

  const handleClose = () => {
    if (extendExamState.state !== "loading") {
      setOpen(false);
      stableOnCancel();
    }
  };

  const handleExtend = () => {
    if (contactState.response?.id && examRecordId) {
      dispatch(extendExam({
        examRecordId: examRecordId,
        contactId: contactState.response.id,
      }));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="extend-exam-dialog-title"
        aria-describedby="extend-exam-dialog-description"
        PaperProps={{
          sx: {
            width: 637,
            height: 244,
            maxWidth: 637,
            maxHeight: 244,
          }
        }}
      >
        <DialogTitle id="extend-exam-dialog-title">
          <Typography variant="h6" component="div">
            Exam Extension Request
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="extend-exam-dialog-description">
            <Typography variant="body1" component="div" sx={{ mb: 2 }}>
              You are about to request an extension for this exam. You will receive a maximum of{" "}
              <strong>{extensionDays} days</strong> extension for the exam.
            </Typography>
            <Typography variant="body1" component="div">
              This extension can only be requested once per exam, and subsequent requests will not be allowed.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            color="warning"
            disabled={extendExamState.state === "loading"}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExtend}
            variant="contained"
            disabled={extendExamState.state === "loading"}
            sx={{
              width: 104,
              height: 32.39,
              textTransform: 'uppercase',
              fontSize: '0.875rem',
              ml: 1,
            }}
            autoFocus
          >
            {extendExamState.state === "loading" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'EXTEND'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Shared loading overlay */}
      <BackgroundLoader
        open={extendExamState.state === "loading"}
        message={extendExamState.backgroundProcessMessage || "Processing exam extension..."}
      />
    </>
  );
}
